/**
 * useCollections Hook
 * Manages user collections persisted via backend API.
 */
import { useState, useEffect, useCallback } from 'react';
import { collectionsAPI } from '../services/api';

export interface CollectionItem {
  id: string;
  name: string;
  type: string;
  itemId?: string | null;  // Optional link to library Item
  createdAt: string;
}

export interface Collection {
  id: string;
  name: string;
  description?: string;
  color: string;
  icon: string;
  items: CollectionItem[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export const useCollections = () => {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch from API on mount
  const fetchCollections = useCallback(async () => {
    try {
      setLoading(true);
      const response = await collectionsAPI.getAll();
      setCollections(response.data);
    } catch (error) {
      console.error('Failed to load collections:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  const addCollection = useCallback(async (data: Omit<Collection, 'id' | 'createdAt' | 'updatedAt' | 'sortOrder' | 'items'>) => {
    try {
      const response = await collectionsAPI.create(data);
      const newCol = response.data;
      setCollections(prev => [...prev, newCol]);
      return newCol;
    } catch (error) {
      console.error('Failed to create collection:', error);
      throw error;
    }
  }, []);

  const updateCollection = useCallback(async (id: string, updates: Partial<Omit<Collection, 'id' | 'createdAt'>>) => {
    try {
      const response = await collectionsAPI.update(id, updates);
      setCollections(prev => prev.map(col => col.id === id ? response.data : col));
    } catch (error) {
      console.error('Failed to update collection:', error);
      throw error;
    }
  }, []);

  const deleteCollection = useCallback(async (id: string) => {
    try {
      await collectionsAPI.delete(id);
      setCollections(prev => prev.filter(col => col.id !== id));
    } catch (error) {
      console.error('Failed to delete collection:', error);
      throw error;
    }
  }, []);

  const reorderCollections = useCallback((reordered: Collection[]) => {
    const updated = reordered.map((col, idx) => ({ ...col, sortOrder: idx }));
    setCollections(updated);
    // Update sort orders in background
    updated.forEach(col => {
      collectionsAPI.update(col.id, { sortOrder: col.sortOrder }).catch(() => {});
    });
  }, []);

  const addItemToCollection = useCallback(async (collectionId: string, item: { name: string; type: string; itemId?: string }) => {
    try {
      const response = await collectionsAPI.addItem(collectionId, item);
      const newItem = response.data;
      setCollections(prev => prev.map(col => {
        if (col.id === collectionId) {
          return { ...col, items: [...col.items, newItem], updatedAt: new Date().toISOString() };
        }
        return col;
      }));
      return newItem;
    } catch (error) {
      console.error('Failed to add item to collection:', error);
      throw error;
    }
  }, []);

  const removeItemFromCollection = useCallback(async (collectionId: string, itemId: string) => {
    try {
      await collectionsAPI.removeItem(collectionId, itemId);
      setCollections(prev => prev.map(col => {
        if (col.id === collectionId) {
          return { ...col, items: col.items.filter(i => i.id !== itemId), updatedAt: new Date().toISOString() };
        }
        return col;
      }));
    } catch (error) {
      console.error('Failed to remove item from collection:', error);
      throw error;
    }
  }, []);

  return {
    collections: [...collections].sort((a, b) => a.sortOrder - b.sortOrder),
    loading,
    addCollection,
    updateCollection,
    deleteCollection,
    reorderCollections,
    addItemToCollection,
    removeItemFromCollection,
  };
};